import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFetch } from '@/hooks/useFetch';
import { sdk } from '@/services/sdk';
import { useOwnerLayoutData } from '@/hooks/useOwnerLayoutData';
import ApperIcon from '@/components/ApperIcon';
import { toast } from 'sonner';

const FIELDS = ['Name', 'service_c', 'customerName_c', 'status_c', 'scheduledAt_c', 'address_c', 'phone_c', 'notes_c'];

export const route = { path: '/', layout: 'owner', access: "profile === 'Worker_c'" };
export const nav = { icon: 'LayoutDashboard', label: 'Dashboard', section: 'Workspace', profiles: ['Worker_c'], order: 1 };

const ACTIVE_STATUSES = ['Assigned', 'Accepted', 'En route', 'En Route', 'Arrived', 'In progress', 'In Progress'];
const DONE_STATUSES = ['Completed', 'Cancelled', 'Rejected'];

export default function Home() {
  const { displayName } = useOwnerLayoutData();
  const [available, setAvailable] = useState(true);
  const [otp, setOtp] = useState(['', '', '', '']);
  const [offerSeconds, setOfferSeconds] = useState(180);
  const [offerExpired, setOfferExpired] = useState(false);
  const [offerBusy, setOfferBusy] = useState(false);
  const { data, loading, error, run } = useFetch(() => sdk.table('jobs_c').select(FIELDS).orderByDesc('CreatedOn').fetch().then((r) => {
    if (!r.success) throw new Error(r.message || 'Could not load service jobs');
    return r.data;
  }), []);
  const jobs = data ?? [];
  const currentJob = useMemo(() => jobs.find((job) => ACTIVE_STATUSES.includes(job.status_c)) || jobs.find((job) => !DONE_STATUSES.includes(job.status_c)) || jobs.find((job) => String(job.status_c).toLowerCase() === 'completed') || null, [jobs]);
  const status = currentJob?.status_c || 'No active job';
  const normalizedStatus = String(status).toLowerCase();
  const isInProgress = normalizedStatus === 'in progress';
  const isArrived = normalizedStatus === 'arrived';
  const isEnRoute = normalizedStatus === 'en route';
  const isAccepted = ['accepted', 'assigned'].includes(normalizedStatus);
  const isCompleted = normalizedStatus === 'completed';
  const startOtpVisible = Boolean(currentJob) && (isArrived || isInProgress);
  const completionOtpVisible = isCompleted;
  const mapQuery = currentJob?.address_c ? encodeURIComponent(currentJob.address_c) : '';
  const greetingName = displayName && displayName !== 'User' ? displayName.split(' ')[0] : 'there';
  const hasOffer = Boolean(currentJob) && ['assigned', 'pending'].includes(normalizedStatus);

  useEffect(() => {
    if (!hasOffer || offerExpired) return undefined;
    const timer = window.setInterval(() => {
      setOfferSeconds((seconds) => {
        if (seconds <= 1) {
          window.clearInterval(timer);
          setOfferExpired(true);
          toast.warning('Demo offer expired. Live nearby-worker reassignment and Agent escalation require the shared dispatch backend.');
          return 0;
        }
        return seconds - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [hasOffer, offerExpired]);

  async function respondToOffer(statusValue) {
    if (!currentJob || offerBusy || offerExpired) return;
    if (statusValue === 'Rejected' && !window.confirm('Reject this order? The live dispatch service must offer it to another worker.')) return;
    setOfferBusy(true);
    try {
      const result = await sdk.table('jobs_c').update({ Id: currentJob.Id, status_c: statusValue });
      if (result.messages?.length) toast.error(result.messages[0]);
      if (!result.data?.length) return;
      setOfferExpired(false);
      toast.success(statusValue === 'Accepted' ? 'Order accepted in demo mode.' : 'Order rejected in demo mode.');
      await run();
    } catch (e) {
      toast.error(e.message || 'Could not update this order.');
    } finally {
      setOfferBusy(false);
    }
  }

  function setOtpDigit(index, value) {
    const digit = value.replace(/\D/g, '').slice(-1);
    setOtp((old) => old.map((item, i) => i === index ? digit : item));
  }

  function requestOtpVerification(mode) {
    if (otp.join('').length !== 4) {
      toast.error('Enter all four OTP digits first.');
      return;
    }
    toast.info('OTP verification is not connected yet. Connect the shared backend and customer/Agent OTP service to verify securely.');
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      <section className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">AryanFix Pro · Worker</p>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Hello, {greetingName} <span aria-hidden="true">✦</span></h1>
          <p className="mt-2 text-sm text-muted-foreground">Your workday, one clear step at a time.</p>
        </div>
        <div className="glass-panel flex items-center justify-between gap-4 rounded-2xl px-4 py-3 sm:min-w-56">
          <div>
            <p className="text-sm font-semibold">Available for work</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{available ? 'Ready for new offers' : 'New offers paused'}</p>
          </div>
          <button type="button" role="switch" aria-checked={available} onClick={() => {
            setAvailable((value) => !value);
            toast.info('Availability is a local preview setting until connected to the shared backend.');
          }} className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${available ? 'bg-success' : 'bg-muted-foreground/40'}`} aria-label="Toggle availability">
            <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${available ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
      </section>

      {error && <div className="glass-panel rounded-2xl border border-destructive/30 p-4 text-sm text-destructive">Could not load job information. <button className="ml-2 underline" onClick={run}>Try again</button></div>}

      <section className="glass-panel overflow-hidden rounded-[26px]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 px-5 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary"><ApperIcon name="BellRing" size={19} /></span>
            <div><p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Order notification</p><h2 className="mt-0.5 text-lg font-bold">{currentJob ? (isAccepted || isEnRoute || isArrived || isInProgress ? 'You have an active order' : 'Order needs your attention') : 'You’re all caught up'}</h2></div>
          </div>
          {currentJob && <span className={`rounded-full px-3 py-1.5 text-xs font-bold ${isCompleted ? 'bg-success-muted text-success' : 'bg-primary/10 text-primary'}`}>{status}</span>}
        </div>
        {hasOffer && <div className="flex flex-col gap-3 border-b border-border/60 bg-warning-muted/45 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex items-start gap-3">
            <ApperIcon name="Timer" size={19} className="mt-0.5 shrink-0 text-warning-foreground" />
            <div>
              <p className="text-sm font-bold">Offer response window · {Math.floor(offerSeconds / 60)}:{String(offerSeconds % 60).padStart(2, '0')}</p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">Demo countdown only. In production, the server starts the 3-minute SLA at assignment and atomically reassigns or escalates on expiry.</p>
              {offerExpired && <p className="mt-1 text-xs font-semibold text-destructive">Expired in this preview — no real reassignment has occurred.</p>}
            </div>
          </div>
          {!offerExpired && <div className="flex flex-wrap gap-2">
            <button type="button" disabled={offerBusy} onClick={() => respondToOffer('Accepted')} className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-success px-4 py-2 text-sm font-bold text-success-foreground disabled:opacity-50">{offerBusy ? 'Saving…' : 'Accept order'}</button>
            <button type="button" disabled={offerBusy} onClick={() => respondToOffer('Rejected')} className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-destructive/30 px-4 py-2 text-sm font-bold text-destructive disabled:opacity-50">Reject</button>
          </div>}
        </div>}
        {loading ? <div className="space-y-3 p-6"><div className="h-5 w-2/3 animate-pulse rounded bg-muted" /><div className="h-4 w-1/2 animate-pulse rounded bg-muted" /></div> : currentJob ? (
          <div className="grid gap-5 p-5 sm:p-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Booking reference</p>
              <p className="mt-1 break-all font-mono text-sm font-semibold">{currentJob.Name || currentJob.Id}</p>
              <p className="mt-3 text-lg font-semibold">{currentJob.service_c || 'Service visit'}</p>
              <p className="mt-1 text-sm text-muted-foreground">{currentJob.scheduledAt_c || 'Appointment time not provided'}</p>
              <p className="mt-2 flex items-start gap-2 text-sm text-muted-foreground"><ApperIcon name="MapPin" size={16} className="mt-0.5 shrink-0" />{currentJob.address_c || 'Service address will appear when authorized'}</p>
            </div>
            <Link to={`/jobs/${currentJob.Id}`} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-bold text-primary-foreground transition hover:opacity-90">Open order <ApperIcon name="ArrowUpRight" size={16} /></Link>
          </div>
        ) : <div className="p-7 text-sm text-muted-foreground">{loading ? 'Loading your work…' : 'No current order is available. New offers will appear here when dispatch is connected.'}</div>}
      </section>

      <section className="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(300px,0.8fr)]">
        <div className="glass-panel rounded-[26px] p-5 sm:p-6">
          <div className="flex items-center justify-between gap-3">
            <div><p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Work status</p><h2 className="mt-1 text-xl font-bold">{currentJob ? status : 'Waiting for assignment'}</h2></div>
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-highlight/15 text-highlight"><ApperIcon name="ClipboardCheck" size={21} /></span>
          </div>
          <div className="mt-6 space-y-4">
            {[
              { label: 'Order assigned', hint: 'Booking received by the worker', done: Boolean(currentJob) },
              { label: 'Accepted & on the way', hint: 'Confirm the visit and travel to the customer', done: isEnRoute || isArrived || isInProgress || isCompleted },
              { label: 'Arrived & start verified', hint: 'Customer start OTP is required when configured', done: isInProgress || isCompleted },
              { label: 'Service completed', hint: 'Finish the work and complete customer confirmation', done: isCompleted }
            ].map((step, index) => (
              <div key={step.label} className="flex gap-3">
                <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full ${step.done ? 'bg-highlight text-highlight-foreground' : 'border border-border bg-background/50 text-muted-foreground'}`}>{step.done ? <ApperIcon name="Check" size={15} /> : <span className="text-xs font-bold">{index + 1}</span>}</span>
                <div className="min-w-0 flex-1 pb-3">
                  <p className={`text-sm font-semibold ${step.done ? '' : 'text-muted-foreground'}`}>{step.label}</p>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">{step.hint}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-panel overflow-hidden rounded-[26px]">
          <div className="flex items-center gap-3 border-b border-border/60 px-5 py-4">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary/10 text-primary"><ApperIcon name="MapPinned" size={19} /></span>
            <div><p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">Map & travel</p><h2 className="font-bold">Service location</h2></div>
          </div>
          {currentJob && mapQuery ? <iframe title="Service location map" src={`https://maps.google.com/maps?q=${mapQuery}&output=embed`} loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="h-56 w-full border-0" /> : <div className="grid h-56 place-items-center px-6 text-center"><div><ApperIcon name="MapPinOff" size={28} className="mx-auto text-muted-foreground" /><p className="mt-3 text-sm font-semibold">Location not available</p><p className="mt-1 text-xs text-muted-foreground">A service address is needed before the map can load.</p></div></div>}
          <div className="flex items-start gap-2 px-5 py-4 text-xs leading-5 text-muted-foreground"><ApperIcon name="Navigation" size={15} className="mt-0.5 shrink-0" />Map shows the job address only. Live worker tracking requires location permission and an authorized backend connection.</div>
        </div>
      </section>

      {(startOtpVisible || completionOtpVisible) && (
        <section className="glass-panel rounded-[26px] p-5 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex gap-3">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary"><ApperIcon name="ShieldCheck" size={21} /></span>
              <div><p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">4-digit verification</p><h2 className="mt-1 text-xl font-bold">{completionOtpVisible ? 'Completion confirmation' : 'Verify service start'}</h2><p className="mt-1 max-w-xl text-sm text-muted-foreground">{completionOtpVisible ? 'A completion OTP must be sent to the customer and verified by the authorized Agent before final closure.' : 'Enter the customer OTP at the start of service when the booking workflow requires it.'}</p></div>
            </div>
            <span className="w-fit rounded-full bg-warning-muted px-3 py-1.5 text-xs font-semibold text-warning-foreground">Backend required</span>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            {otp.map((digit, index) => <input key={index} aria-label={`OTP digit ${index + 1}`} inputMode="numeric" autoComplete={index === 0 ? 'one-time-code' : 'off'} maxLength={1} value={digit} onChange={(event) => setOtpDigit(index, event.target.value)} className="h-14 w-12 rounded-xl border border-border/80 bg-background/65 text-center font-heading text-2xl font-bold outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20" />)}
            <button type="button" onClick={() => requestOtpVerification(completionOtpVisible ? 'completion' : 'start')} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90"><ApperIcon name="ShieldCheck" size={16} /> Verify OTP</button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">OTP values are never displayed here. Sending, expiry, and Agent verification are not active until the shared backend and OTP provider are connected.</p>
        </section>
      )}

      {!currentJob && !loading && <section className="glass-panel flex items-center gap-4 rounded-[26px] p-5"><span className="grid h-11 w-11 place-items-center rounded-xl bg-highlight/15 text-highlight"><ApperIcon name="CheckCircle2" size={21} /></span><div><h2 className="font-semibold">Ready for your next job</h2><p className="mt-1 text-sm text-muted-foreground">Keep availability on to receive dispatch offers when the backend is connected.</p></div></section>}
    </div>
  );
}
